import { useState, useEffect, useContext, useCallback } from "react";
import GlobalContext from "../common/Context";
import * as constants from "../../constants";

const INITIAL_STATE = {
  company: null,
  profile: null,
  authUser: null,
};

const initAuthObj = () => ({
  auth: null,
  token: null,
  tokenFetched: false,
  settingClaim: false,
  claimSet: false,
  profile: null,
  profileFetching: false,
  profileFetched: false,
  mustAddprofile: false,
  profileAdded: false,
  userInfo: null,
  userInfoSet: false,
  authDone: false,
  forceToken: false,
  company: null,
  companyFetched: true,
});

const useFirebaseAuthentication = (fb) => {
  const { firebaseState, setSignLoad, setSignError, setCurrentUser } =
    useContext(GlobalContext);

  const [authUser, setAuthUser] = useState(INITIAL_STATE);
  const [appFirstLoading, setAppFirstLoading] = useState(true);
  const [authObj, setAuthObj] = useState(initAuthObj);

  const resetAuth = useCallback(() => {
    setAuthUser(INITIAL_STATE);
    setAuthObj(initAuthObj());
    setSignLoad(false);
    setAppFirstLoading(false);
    sessionStorage.clear();
  }, [setSignLoad]);

  const badSignIn = useCallback(async () => {
    await firebaseState.doSignOut();
    setSignError(constants.SIGNIN_NO_ACCESS);
    resetAuth();
  }, [firebaseState, setSignError, resetAuth]);

  const getToken = useCallback(async () => {
    try {
      const token = await authObj.auth?.getIdTokenResult(authObj.forceToken);
      setAuthObj((prev) => ({
        ...prev,
        token,
        tokenFetched: true,
        forceToken: false,
      }));
    } catch (e) {
      console.error(`Auth error: getIdTokenResult(${authObj.forceToken})`);
      console.error(e);
    }
  }, [authObj.auth, authObj.forceToken]);

  const getDashboardUser = useCallback(async () => {
    setSignLoad(false);
    setAuthObj((prev) => ({ ...prev, profileFetching: true }));
    try {
      const user = await firebaseState.getProfile(
        authObj.auth?.email || authObj.token?.claims.user.email
      );
      !user
        ? await badSignIn()
        : setAuthObj((prev) => ({
            ...prev,
            profile: user,
            profileFetched: true,
          }));
    } catch (e) {
      console.error(e);
    }
  }, [firebaseState, authObj.auth, setSignLoad, authObj.token, badSignIn]);

  const getCompany = useCallback(async () => {
    try {
      const company = await firebaseState.getCompanyById(
        authObj.profile.uidCompany
      );
      setAuthObj((prev) => ({ ...prev, company, companyFetched: true }));
    } catch (e) {
      console.error(e);
    }
  }, [authObj.profile, firebaseState]);

  const finishAuth = useCallback(() => {
    setSignError("");
    setAuthUser(authObj.userInfo);
    setAppFirstLoading(false);
    setAuthObj((prev) => ({ ...prev, authDone: true }));
    setCurrentUser((prev) => ({
      ...prev,
      authUser: authObj.auth,
      ...authObj.userInfo,
    }));
  }, [authObj.auth, authObj.userInfo, setCurrentUser, setSignError]);

  /* First step, get token (or refresh token if custom claims set) */
  useEffect(() => {
    authObj.auth && !authObj.tokenFetched && getToken();
  }, [authObj.auth, authObj.tokenFetched, getToken]);

  /* Get Dashboard user by token */
  useEffect(() => {
    !authObj.profileFetched &&
      !authObj.profileFetching &&
      authObj.tokenFetched &&
      (authObj.token?.claims?.bi || authObj.token?.claims?.bia) &&
      getDashboardUser();
  }, [
    authObj.token,
    authObj.tokenFetched,
    authObj.profileFetching,
    authObj.profileFetched,
    getDashboardUser,
  ]);

  /* Get user company */
  useEffect(() => {
    authObj.profileFetched && authObj.profile && getCompany();
  }, [authObj.profileFetched, authObj.profile, getCompany]);

  /* Set user info */
  useEffect(() => {
    authObj.companyFetched &&
      authObj.company &&
      setAuthObj((prev) => ({
        ...prev,
        userInfo: {
          company: authObj.company,
          profile: authObj.profile,
          authUser: authObj.auth,
          permissions: authObj.token?.claims,
        },
        userInfoSet: true,
      }));
  }, [authObj.auth, authObj.company, authObj.companyFetched, authObj.profile]);

  /* Last Step */
  useEffect(() => {
    !authObj.authDone && authObj.userInfoSet && finishAuth();
  }, [authObj.authDone, authObj.userInfoSet, finishAuth]);

  /* Setup */
  useEffect(() => {
    const fetchData = async (auth) => {
      setAuthObj({ ...initAuthObj(), auth });
    };
    const unlisten = fb
      .auth()
      .onAuthStateChanged((auth) => (auth ? fetchData(auth) : resetAuth()));
    return () => {
      unlisten();
    };
  }, [fb, resetAuth]);

  return { authUser, appFirstLoading };
};

export default useFirebaseAuthentication;
